import React, {
  createContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { setUnauthorizedHandler } from '../api/api';
import buyerAuthService from '../api/buyerAuthService';
import { consumeAutologinToken } from '../helpers/autologin';

export const BuyerAuthContext = createContext(null);

const ENFORCEMENT = {
  OFF: 'off',
  OBSERVE: 'observe',
  ENFORCE: 'enforce',
};

const INITIAL = {
  status: 'bootstrapping',
  authenticated: false,
  enforcement: ENFORCEMENT.OFF,
  gateRequired: false,
  verifiable: true,
  maskedPhone: null,
  resendAt: null,
  attemptsLeft: null,
  error: null,
  busy: false,
};

/**
 * Holds whether this buyer has proved who they are, and what the server wants
 * done about it.
 *
 * The rule that matters here: this provider FAILS OPEN. If the probe 5xxs or
 * the network drops, the page renders. Enforcement happens on the server — the
 * data endpoints refuse on their own — so a client that failed closed would
 * block paying students and add no security whatsoever.
 */
function BuyerAuthProvider({ gate, children }) {
  const [state, setState] = useState(INITIAL);

  // StrictMode double-invokes effects in development. The bootstrap is
  // idempotent, but re-running it on every render would put a probe on the
  // critical path of each one.
  const bootstrappedFor = useRef(null);

  const resourceKey = gate ? `${gate.resourceType}:${gate.resourceId}` : 'none';

  const refresh = useCallback(async () => {
    try {
      // A token in the URL is spent first, then the probe reports the result.
      // Any failure here falls through to the probe on purpose: an expired or
      // already-used link should land the student on the OTP screen, not on an
      // error page telling them their link is broken.
      const token = consumeAutologinToken();
      if (token && gate) {
        await buyerAuthService.autologin({ ...gate, token }).catch(() => null);
      }

      const data = await buyerAuthService.getSession(gate || {});
      setState((prev) => ({
        ...prev,
        status: 'ready',
        authenticated: Boolean(data?.authenticated),
        enforcement: data?.enforcement || ENFORCEMENT.OFF,
        gateRequired: Boolean(data?.gateRequired),
        verifiable: data?.verifiable !== false,
        maskedPhone: data?.maskedPhone || null,
      }));
    } catch {
      // Fail open. See the note above — this is deliberate.
      setState((prev) => ({ ...prev, status: 'ready', enforcement: ENFORCEMENT.OFF }));
    }
  }, [gate]);

  useEffect(() => {
    if (bootstrappedFor.current === resourceKey) return;
    bootstrappedFor.current = resourceKey;
    refresh();
  }, [resourceKey, refresh]);

  // One expired session can be discovered by any of several in-flight
  // requests. Flipping the gate on is idempotent, so they can all report it.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setState((prev) => ({
        ...prev,
        authenticated: false,
        gateRequired: true,
        enforcement: ENFORCEMENT.ENFORCE,
      }));
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const sendOtp = useCallback(async () => {
    if (!gate) return;
    setState((prev) => ({ ...prev, busy: true, error: null }));
    try {
      const data = await buyerAuthService.sendOtp(gate);
      setState((prev) => ({
        ...prev,
        busy: false,
        maskedPhone: data?.maskedPhone || prev.maskedPhone,
        attemptsLeft: data?.attemptsAllowed ?? prev.attemptsLeft,
        // An absolute instant, not a countdown the client owns, so reloading
        // the page mid-cooldown resumes rather than resets it.
        resendAt: Date.now() + ((data?.resendAfterSeconds || 0) * 1000),
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        busy: false,
        error: { code: err?.code, message: err?.message },
        resendAt: err?.errorDetails?.retryAfterSeconds
          ? Date.now() + (err.errorDetails.retryAfterSeconds * 1000)
          : prev.resendAt,
      }));
    }
  }, [gate]);

  const verifyOtp = useCallback(async (code) => {
    if (!gate) return false;
    setState((prev) => ({ ...prev, busy: true, error: null }));
    try {
      await buyerAuthService.verifyOtp({ ...gate, code });
      setState((prev) => ({
        ...prev,
        busy: false,
        authenticated: true,
        gateRequired: false,
        error: null,
      }));
      return true;
    } catch (err) {
      setState((prev) => ({
        ...prev,
        busy: false,
        error: { code: err?.code, message: err?.message },
        attemptsLeft: err?.errorDetails?.attemptsLeft ?? prev.attemptsLeft,
      }));
      return false;
    }
  }, [gate]);

  const value = useMemo(() => ({
    ...state, gate, sendOtp, verifyOtp, refresh,
  }), [state, gate, sendOtp, verifyOtp, refresh]);

  return (
    <BuyerAuthContext.Provider value={value}>
      {children}
    </BuyerAuthContext.Provider>
  );
}

BuyerAuthProvider.propTypes = {
  gate: PropTypes.shape({
    resourceType: PropTypes.string.isRequired,
    resourceId: PropTypes.string.isRequired,
  }),
  children: PropTypes.node.isRequired,
};

BuyerAuthProvider.defaultProps = {
  gate: null,
};

export { ENFORCEMENT };
export default BuyerAuthProvider;
